/**
 * i18n 入口 — react-i18next + browser language detector
 *
 * 首屏仅打包 zh / en；其余语言按需 dynamic import。
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import { SUPPORTED_LANGS, normalizeUiLang, type UiLangCode } from './locale-config'
import zh from './locales/zh'
import en from './locales/en'

export { SUPPORTED_LANGS, UI_LANGS, uiLangToApiLocale, uiLangToIntlLocale, normalizeUiLang } from './locale-config'
export type { UiLangCode, UiLangDef } from './locale-config'

export type SupportedLang = UiLangCode

const LANG_CODES = SUPPORTED_LANGS.map((l) => l.code)

const LAZY_LOCALES: Partial<Record<UiLangCode, () => Promise<{ default: Record<string, unknown> }>>> = {
  fr: () => import('./locales/fr'),
  de: () => import('./locales/de'),
  es: () => import('./locales/es'),
  pt: () => import('./locales/pt'),
  it: () => import('./locales/it'),
  id: () => import('./locales/id'),
  ko: () => import('./locales/ko'),
  ja: () => import('./locales/ja'),
  ar: () => import('./locales/ar'),
}

const loadedLocales = new Set<UiLangCode>(['zh', 'en'])

export async function ensureUiLocaleLoaded(code: string): Promise<UiLangCode> {
  const base = normalizeUiLang(code)
  if (loadedLocales.has(base)) return base
  const loader = LAZY_LOCALES[base]
  if (!loader) return base
  const mod = await loader()
  i18n.addResourceBundle(base, 'translation', mod.default, true, true)
  loadedLocales.add(base)
  return base
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      zh: { translation: zh },
      en: { translation: en },
    },
    fallbackLng: (code) => {
      const base = normalizeUiLang(typeof code === 'string' ? code : 'zh')
      if (base === 'zh') return ['zh']
      if (base === 'en') return ['en', 'zh']
      return [base, 'en', 'zh']
    },
    supportedLngs: LANG_CODES,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'silkreels.lang',
      caches: ['localStorage'],
      convertDetectedLanguage: (lng) => normalizeUiLang(lng),
    },
  })

/** 同步 <html lang/dir>,阿拉伯语启用 RTL */
function applyDocumentLang(code: string) {
  if (typeof document === 'undefined') return
  const base = normalizeUiLang(code)
  document.documentElement.lang = base
  document.documentElement.dir = base === 'ar' ? 'rtl' : 'ltr'
}

applyDocumentLang(i18n.language)
i18n.on('languageChanged', applyDocumentLang)

void ensureUiLocaleLoaded(i18n.language).then((code) => {
  if (code !== normalizeUiLang(i18n.language)) return
  if (i18n.language !== code) {
    void i18n.changeLanguage(code)
  }
})

i18n.on('languageChanged', (lng) => {
  void ensureUiLocaleLoaded(lng)
})

export default i18n
